import { navigateTo } from 'front-end/src/app/util/appHistory'
import {
  Box,
  ButtonOutline,
  Flex,
  FullBlockSpinner
} from 'front-end/src/components'
import IconRefresh from 'front-end/src/components/common/icon/IconRefresh'
import PageHeader from 'front-end/src/components/common/page/header/PageHeader'
import { getQuillStyle } from 'front-end/src/components/common/quill/EditorQuill'
import { TableExtended } from 'front-end/src/components/common/table-extended/TableExtended'
import Tags from 'front-end/src/components/common/tag/Tags'
import { RecurrenceText } from 'front-end/src/pages/manage-events/components/recurrence-text/RecurrenceText'
import { DangerouslySetInnerHTML } from 'front-end/src/utils/DangerouslySetInnerHTML'
import React from 'react'
import { RouteComponentProps } from 'react-router-dom'
import { brandColors } from 'shared/basicPalette'
import dayjs from 'shared/dayjs'
import { linkBuilder } from 'shared/linkBuilder'

import { Trans } from '@lingui/macro'
import DateIcon from '../date-icon/DateIcon'
import EventStatus, { StatusOfEventProps } from '../event-status/EventStatus'
import {
  useEventDetailSeriesQuery,
  useRegisterToAttendWholeSeriesMutation
} from './EventDetailSeries.codegen'
import {
  Page,
  PageContentWrapper
} from 'front-end/src/components/common/page/Page'
import EventTopbar from './EventTopbar'
import IconDot from 'front-end/src/components/common/icon/IconDot'
import IconGlobe from 'front-end/src/components/common/icon/IconGlobe'
import IconLocationPin from 'front-end/src/components/common/icon/IconLocationPin'
import { formatEventLocation } from '../../eventUtils'
import { isUrl } from 'front-end/src/utils/url'
import { fontSizes } from 'front-end/src/components/chakraThemeExtension'
import IconClock from 'front-end/src/components/common/icon/IconClock'
import { RecurringEventAttendanceTypeEnum } from 'front-end/src/graphql/public/PublicSchemaTypes'
import { isRegistrationClosed } from '../event-attendance/EventAttendance'
import { notification } from 'front-end/src/components/common/notifications/Notifications'
import { RSVPButton } from '../rsvp-buttons/RSVPButtons'
import { SQLOrderDirectionEnum } from 'front-end/../shared/enums/common'
import EventFormattedTime from 'front-end/../shared/components/event-formatted-time/EventFormattedTime'
import { useEventsTimezoneQuery } from '../event-card/EventCard.codegen'

export const EventDetailSeriesPage: React.FC<
  RouteComponentProps<{
    eventId: string
  }>
> = (props) => {
  const { params } = props.match
  const eventId = Number(params.eventId)

  const { data, loading, refetch } = useEventDetailSeriesQuery({
    variables: {
      eventId,
      order: {
        name: 'start_time',
        sort: SQLOrderDirectionEnum.ASC
      }
    }
  })
  const seriesData = data?.recurringEvent || null
  const { data: eventsTimezone } = useEventsTimezoneQuery()

  const [registerToAttendWholeSeries, { loading: loadingRegisterSeries }] =
    useRegisterToAttendWholeSeriesMutation({
      onCompleted: refetch,
      onError: (e) => {
        notification.error(e.response.data.message)
      }
    })

  const columns = React.useMemo(
    () => [
      {
        width: '72px',
        align: 'center',
        Cell: ({ row }) => (
          <DateIcon
            date={dayjs(row.original.start_time)}
            dataTest="EventDate"
          />
        ),
        accessor: 'start_time'
      },
      {
        accessor: 'title',
        Cell: ({ row }) => (
          <>
            <Flex direction="column">
              <Box fontSize="18px" fontWeight="500" data-test="EventTitle">
                {row.original.title}
              </Box>
              <Box
                mt="4px"
                color={brandColors.grey[400]}
                fontSize={fontSizes.ssm}
                alignItems="center"
                data-test="EventTimeAndLocation"
              >
                <>
                  <IconClock mr="8px" />
                  <EventFormattedTime
                    event={row.original}
                    timezone={eventsTimezone?.me.timezone}
                  />
                </>
              </Box>
              <Box color={brandColors.grey[400]} fontSize={fontSizes.ssm}>
                {row.original.location && (
                  <>
                    {isUrl(row.original.location) ? (
                      <IconGlobe mr="8px" />
                    ) : (
                      <IconLocationPin mr="8px" />
                    )}
                    {formatEventLocation(row.original.location)}
                  </>
                )}
              </Box>
              <Box mt="4px">
                <Tags
                  items={row.original.tags}
                  onClick={(eventTag) => {
                    navigateTo(linkBuilder.eventTag(eventTag.id))
                  }}
                />
              </Box>
            </Flex>
          </>
        )
      },
      {
        accessor: 'status',
        align: seriesData?.published_date ? 'left' : 'right',
        width: '110px',
        Cell: ({ row }) => (
          <EventStatus alignItems="center" event={row.original} />
        )
      },
      {
        accessor: 'rsvp',
        align: 'right',
        width: seriesData?.published_date ? '220px' : '0',
        Cell: ({ row }) => (
          <Flex alignItems="center">
            {seriesData?.published_date && (
              <>
                <Box color={brandColors.grey[700]} fontSize="14px" mr="16px">
                  {!isRegistrationClosed(row.original) && 'Going?'}
                </Box>
                <RSVPButton event={row.original} refetch={refetch} />
              </>
            )}
          </Flex>
        )
      }
    ],
    [data]
  )

  if (!seriesData) return <></>

  return (
    <>
      <EventTopbar
        title={seriesData.title || ''}
        eventId={seriesData.id}
        isSeries
      />
      <Page withTopbar bgColor={brandColors.white}>
        {loading ? (
          <FullBlockSpinner />
        ) : (
          <>
            <PageContentWrapper maxW="964px">
              <PageHeader
                fullWidth
                className="PageHeader"
                title={seriesData?.title}
                showBackButton={false}
                controls={
                  <EventStatus
                    ml="6px"
                    as="span"
                    event={seriesData as StatusOfEventProps}
                  />
                }
                subHeader={
                  <>
                    <Flex
                      mt="4px"
                      fontSize="12px"
                      color={brandColors.grey[500]}
                    >
                      {seriesData.recurrence && (
                        <>
                          <IconRefresh fontSize="16px" mr="6px" />
                          <Box>
                            <RecurrenceText
                              recurrence={seriesData.recurrence}
                            />
                          </Box>
                        </>
                      )}
                      {seriesData.author && (
                        <>
                          <IconDot m="0 4px" />
                          Created by {seriesData.author.display_name}
                        </>
                      )}
                    </Flex>
                    {seriesData.description && (
                      <Flex
                        mt="24px"
                        css={getQuillStyle('ql-editor')}
                        className="ql-editor"
                        color={brandColors.black}
                        maxW="630px"
                      >
                        <DangerouslySetInnerHTML
                          content={seriesData.description as string}
                        />
                      </Flex>
                    )}
                  </>
                }
              />

              {seriesData.events && (
                <Box mt="40px">
                  {seriesData.attendance_type ===
                    RecurringEventAttendanceTypeEnum.all &&
                    seriesData.published_date && (
                      <Flex justifyContent="flex-end">
                        <Flex justifyContent="center" w="140px" p="16px 8px">
                          <ButtonOutline
                            backgroundColor="white"
                            onClick={() => {
                              registerToAttendWholeSeries({
                                variables: {
                                  eventId: seriesData.id
                                }
                              })
                            }}
                            isLoading={loadingRegisterSeries}
                          >
                            <Trans>Yes to All</Trans>
                          </ButtonOutline>
                        </Flex>
                      </Flex>
                    )}
                  <TableExtended
                    m="0 16px"
                    data={seriesData.events}
                    dataCount={seriesData.events.length}
                    loading={loading}
                    columns={columns}
                    noTableHead
                    onRowClick={(row) => {
                      navigateTo(linkBuilder.eventDetail(row?.original.id))
                    }}
                    dataTest="RecurringEventsTable"
                  />
                </Box>
              )}
            </PageContentWrapper>
          </>
        )}
      </Page>
    </>
  )
}
